/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from 'react';
import usePlacesAutocomplete from 'use-places-autocomplete';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { XCircleIcon, ArrowLeftIcon, MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Loader from '../Loader';
import { GpsIcon } from '../../utils/svg';
import { GEOLOCATION_PERMISSION, centerLocation } from '../../utils/constants';
import Footer from '../Footer';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../store/reducers/user';
import { getCurrentLocation, getDetailLocationFromLatLong, isEmptyData, sendAnalyticsPageView } from '../../utils/helper';

const mapOptions = {
	disableDefaultUI: true,
	zoomControl: false,
	mapTypeControl: false,
	fullscreenControl: false,
	streetViewControl: false,
	rotateControl: false,
	gestureHandling: 'greedy',
};

const CustomGoogleMap = (props) => {
	const { onClose, isFormAddress } = props;
	const dispatch = useDispatch();

	const {
		ready,
		suggestions: { status, data },
		setValue,
		clearSuggestions,
	} = usePlacesAutocomplete({
		requestOptions: { types: [] },
	});

	const { coords, permission: permissionGeolocation } = useSelector((store) => store?.gps);
	const [form, setForm] = useState({
		zoom: 4,
		name: null,
		address: null,
		latitude: null,
		longitude: null,
		country: null,
		state: null,
		city: null,
		village: null,
		district: null,
		postalCode: null,
		detail: null,
	});

	const [isLoading, setIsLoading] = useState(true);
	const [isVisibleFooter, setIsVisibleFooter] = useState(false);
	const [inputValue, setInputValue] = useState('');

	useEffect(() => {
		sendAnalyticsPageView('maps_session');
	}, []);

	useEffect(() => {
		if (ready) {
			setIsLoading(false);
		}
	}, [ready]);

	const onLoad = useCallback(() => {
		setTimeout(() => {
			const elementsToHide = document.querySelectorAll('.gmnoprint');
			elementsToHide.forEach((el) => (el.style.display = 'none'));
		}, 1000);
	}, []);

	const handleOnClick = async (data) => {
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const place_id = data?.placeId;
		const formated = await getDetailLocationFromLatLong(lat, lng, place_id);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleDragEnd = async (data = null) => {
		const lat = data?.latLng?.lat();
		const lng = data?.latLng?.lng();
		const formated = await getDetailLocationFromLatLong(lat, lng);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleSearch = () => {
		if (inputValue.trim().length < 3) return;

		setValue(inputValue);
	};

	const handleCloseSearch = () => {
		setInputValue('');
		setValue('');
		setIsVisibleFooter(false);
		setForm({
			zoom: 4,
			name: null,
			address: null,
			lat: null,
			lng: null,
		});
		clearSuggestions();
	};

	const handleSelect = async (data) => {
		const place_id = data?.place_id;
		const formated = await getDetailLocationFromLatLong(null, null, place_id);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const handleConfirmLocation = (data = null) => {
		if (isEmptyData(data?.latitude) || isEmptyData(data?.longitude)) return;
		const address = {
			addressName: null,
			address: data?.address,
			latitude: data?.latitude,
			longitude: data?.longitude,
			country: data?.country,
			state: data?.state,
			city: data?.city,
			village: data?.village,
			district: data?.district,
			postalCode: data?.postalCode ?? '',
			detail: data?.detail,
		};
		if (!isFormAddress) {
			dispatch(setUser(address));
		}
		onClose(isFormAddress ? address : null);
	};

	const handleGetCurrentLocation = async () => {
    const coords = await getCurrentLocation();
		const formated = await getDetailLocationFromLatLong(coords?.latitude, coords?.longitude);
		setForm({
			zoom: 15,
			name: formated?.name,
			address: formated?.address,
			latitude: formated?.lat,
			longitude: formated?.lng,
			country: formated?.country,
			state: formated?.state,
			city: formated?.city,
			village: formated?.village,
			district: formated?.district,
			postalCode: formated?.postalCode ?? '',
			detail: formated?.detail,
		});
		setValue(formated?.address);
		setIsVisibleFooter(true);
	};

	const renderSuggestions = () => {
		return data.map((suggestion) => {
			const {
				place_id,
				structured_formatting: { main_text, secondary_text },
			} = suggestion;
			if (!secondary_text) {
				return null;
			}
			return (
				<div key={place_id} onClick={() => handleSelect(suggestion)}>
					<div className='cursor-pointer p-2 border-b-1 text-gray-900 text-xs hover:bg-blue-500 hover:text-white'>{`${main_text} - ${secondary_text}`}</div>
				</div>
			);
		});
	};

	const center = useMemo(() => {
		if (isEmptyData(form?.latitude) || isEmptyData(form?.longitude)) return centerLocation;
		return {
			lat: form?.latitude,
			lng: form?.longitude,
		};
	}, [form?.latitude, form?.longitude]);

	return (
		<>
			<style>
				{`
					[data-rsbs-header] {
						display: none !important;
					}
				`}
			</style>
			{isLoading ? (
				<Loader />
			) : (
				<>
					<div className='h-screen w-full flex justify-center items-center'>
						<div className='relative h-screen w-full'>
							<div className='absolute z-10 mt-3 w-full'>
								<div className='flex mx-4'>
									<div id='back_btn_maps' className='mr-2 flex justify-center items-center cursor-pointer' onClick={onClose}>
										<ArrowLeftIcon className='h-7 w-7 text-gray-900 bg-white rounded-full p-1.5' />
									</div>
									<div className='flex items-center rounded-full border border-solid border-gray-200 h-8 gap-3 bg-white w-full'>
										<MagnifyingGlassIcon className={`h-7 w-7 min-w-7 min-h-7 pl-2 text-gray-600`} strokeWidth={3} />
										<input
											placeholder='Cari alamat atau nama tempat'
											value={inputValue}
											onChange={(e) => setInputValue(e.target.value)}
											className='flex-auto w-full m-0 bg-transparent border-none outline-none text-xs font-normal'
											disabled={!ready}
										/>
										{!isEmptyData(inputValue) && <XCircleIcon onClick={() => handleCloseSearch()} className='h-7 w-7 text-gray-500' />}
										<div onClick={handleSearch} className={`${!isEmptyData(inputValue) ? 'bg-[var(--rose-violet)] text-white' : 'bg-gray-400 text-white'} text-xs px-3 py-1.5 rounded-full`}>
											Cari
										</div>
									</div>
								</div>
								{status === 'OK' && !isVisibleFooter && <div className='my-2 mx-4 border-1 rounded-lg bg-white'>{renderSuggestions()}</div>}
							</div>
							<GoogleMap
								mapContainerClassName='h-full w-full'
								clickableIcons
								center={center}
								zoom={form?.zoom}
								onClick={(e) => {
									handleOnClick(e);
								}}
								onLoad={onLoad}
								options={mapOptions}>
								{center && (
									<Marker
										position={center}
										draggable
										onDragEnd={(e) => {
											handleDragEnd(e);
										}}
									/>
								)}
							</GoogleMap>
							<Footer className='!shadow-none'>
								<div className='flex justify-end mb-5 mr-3'>
									<div
										id='maps_current_location'
										className={`bg-white p-2 rounded-full cursor-pointer ${(!permissionGeolocation || permissionGeolocation === GEOLOCATION_PERMISSION.DENIED) && 'hidden'}`}
										onClick={handleGetCurrentLocation}>
										<GpsIcon className={'stroke-blue-500'} />
									</div>
								</div>
								<div className={`bg-white p-4 rounded-t-2xl transition-transform duration-500 ease-in-out  shadow-custom ${isVisibleFooter ? 'translate-y-0 relative' : 'translate-y-full hidden'}`}>
									<div className='flex justify-center'>
										<div className='bg-gray-300 h-1.5 w-9 rounded-full' />
									</div>
									<div className='mt-4 flex justify-between items-center'>
										<div className='text-gray-800 text-sm font-semibold'>Pilih Lokasi</div>
										<button className='border-1 border-byzantium py-1.5 px-4 text-base font-semibold text-purple-700 hidden'>
											Ubah
										</button>
									</div>
									<div className='mt-4 flex items-center'>
										<div className='pr-3'>
											<MapPinIcon className='h-6 w-6 text-gray-400' />
										</div>
										<div>
											<div className='text-gray-800 text-base font-semibold'>{form?.name}</div>
											<div className='text-xs text-gray-500'>{form?.address}</div>
										</div>
									</div>
									<button id='maps_confirm_location' className='bg-[var(--rose-violet)] w-full text-white font-normal text-sm py-3 border-0 mt-5' onClick={() => handleConfirmLocation(form)}>
										Simpan Lokasi
									</button>
								</div>
							</Footer>
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default CustomGoogleMap;
